import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Sparkles, Trash2, Mail, History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SEO from "@/components/SEO";

export default function AdminNewsletter() {
    const [subs, setSubs] = useState([]);
    const [logs, setLogs] = useState([]);
    const [tab, setTab] = useState("subscribers");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ subject: "", message: "", use_ai: false, ai_prompt: "" });
    const [busy, setBusy] = useState(false);

    const load = () => Promise.all([
        api.get("/admin/newsletter/subscribers").then((r) => setSubs(r.data)),
        api.get("/admin/newsletter/logs").then((r) => setLogs(r.data)),
    ]);
    useEffect(() => { load(); }, []);

    const send = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/admin/newsletter/send", form);
            toast.success(`Newsletter sent to ${data.count} subscribers!`);
            setOpen(false); setForm({ subject: "", message: "", use_ai: false, ai_prompt: "" }); load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const delSub = async (id) => { if (confirm("Remove subscriber?")) { await api.delete(`/admin/newsletter/subscribers/${id}`); load(); } };

    return (
        <div>
            <SEO title="Admin - Newsletter" />
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-brand-navy">Newsletter</h1>
                    <p className="text-sm text-brand-navy/55">{subs.length} subscribers</p>
                </div>
                <button onClick={() => setOpen(true)} className="btn-accent px-4 py-2 rounded-lg inline-flex items-center gap-2 font-semibold" data-testid="open-bulk-send"><Send className="w-4 h-4" /> Bulk Send</button>
            </div>
            <div className="flex gap-2 mb-4">
                <button onClick={() => setTab("subscribers")} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === "subscribers" ? "btn-primary" : "border border-brand-light"}`}><Mail className="w-3.5 h-3.5 inline mr-1.5" /> Subscribers</button>
                <button onClick={() => setTab("logs")} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === "logs" ? "btn-primary" : "border border-brand-light"}`}><History className="w-3.5 h-3.5 inline mr-1.5" /> Send History</button>
            </div>

            {tab === "subscribers" ? (
                <div className="bg-white rounded-2xl border border-brand-light overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                        <thead className="bg-brand-light/40"><tr><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Subscribed</th><th></th></tr></thead>
                        <tbody>
                            {subs.map((s) => (
                                <tr key={s.id} className="border-t border-brand-light">
                                    <td className="px-4 py-3 font-mono text-xs">{s.email}</td>
                                    <td className="px-4 py-3">{s.name || "—"}</td>
                                    <td className="px-4 py-3 text-xs">{s.created_at?.slice(0, 10)}</td>
                                    <td className="px-4 py-3 text-right"><button onClick={() => delSub(s.id)} className="p-1.5 hover:bg-brand-red/10 text-brand-red rounded"><Trash2 className="w-3.5 h-3.5" /></button></td>
                                </tr>
                            ))}
                            {subs.length === 0 && <tr><td colSpan={4} className="text-center py-12 text-brand-navy/55">No subscribers yet</td></tr>}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="space-y-3">
                    {logs.map((l) => (
                        <div key={l.id} className="bg-white border border-brand-light rounded-2xl p-5">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="font-bold text-brand-navy">{l.subject}</div>
                                <div className="text-xs text-brand-navy/55">Sent to {l.count} • {l.created_at?.slice(0, 10)}</div>
                            </div>
                            <p className="text-sm text-brand-navy/75 mt-2 whitespace-pre-line line-clamp-4">{l.message}</p>
                        </div>
                    ))}
                    {logs.length === 0 && <p className="text-center py-12 text-brand-navy/55">No newsletters sent yet</p>}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="font-bn max-w-2xl w-[95vw] sm:w-full z-[100]">
                    <DialogHeader><DialogTitle>Send Newsletter</DialogTitle></DialogHeader>
                    <form onSubmit={send} className="space-y-3">
                        <div><label className="block text-xs mb-1 font-medium">Subject *</label><input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-brand-light" data-testid="newsletter-subject" /></div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={form.use_ai} onChange={(e) => setForm({ ...form, use_ai: e.target.checked })} data-testid="use-ai-checkbox" />
                            <Sparkles className="w-4 h-4 text-brand-red" /> Generate message with AI (Claude)
                        </label>
                        {form.use_ai ? (
                            <div><label className="block text-xs mb-1 font-medium">AI Prompt (e.g. "Announce 30% Eid sale on math books")</label><textarea rows={3} required value={form.ai_prompt} onChange={(e) => setForm({ ...form, ai_prompt: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-brand-light font-bn" data-testid="ai-prompt" /></div>
                        ) : (
                            <div><label className="block text-xs mb-1 font-medium">Message *</label><textarea rows={6} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-brand-light font-bn" data-testid="newsletter-message" /></div>
                        )}
                        <div className="text-xs text-brand-navy/55 bg-yellow-50 border border-yellow-200 p-2.5 rounded-lg">⚠️ NOTE: Email delivery is currently MOCKED — messages are logged only. Connect Resend or SendGrid for real sending.</div>
                        <button disabled={busy} className="btn-accent w-full py-2.5 rounded-lg font-semibold disabled:opacity-60" data-testid="newsletter-send-btn">{busy ? "Sending..." : `Send to ${subs.length} subscribers`}</button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
